import { Loading } from '@arcadiapower/shrike';
import { UserMachine } from '~/machines/user-creation';
import { UtilityMachine } from '~/machines/utility-selection';
import { MachineScreenStates, ScreenMap } from '~/machines/utils/types';
import { AccountNumberContainer } from '~/screens/account-number/account-number.container';
import { Acuity } from '~/screens/acuity';
import { BillUploadContainer } from '~/screens/bill-upload/bill-upload.container';
import { BillingContainer } from '~/screens/billing/billing.container';
import { ContractsUploadContainer } from '~/screens/contracts-upload/contracts-upload.container';
import { CreateAccountGoogleContainer } from '~/screens/create-account-google/create-account-google.container';
import { CreateAccountContainer } from '~/screens/create-account/create-account.container';
import { CredentialsContainer } from '~/screens/credentials/credentials.container';
import { CsUnavailableContainer } from '~/screens/cs-unavailable/cs-unavailable.container';
import { DocuSignContainer } from '~/screens/docu-sign/docu-sign.container';
import { EnergywellPaymentVerificationFailedContainer } from '~/screens/energywell-payment-verification-failed/energywell-payment-verification-failed.container';
import { Friction, Unexpected } from '~/screens/errors';
import { LMIProofDocsUploadContainer } from '~/screens/lmi-proof-docs-upload/lmi-proof-docs-upload.container';
import { LmiReviewContainer } from '~/screens/lmi-review/lmi-review.container';
import { CardContainer } from '~/screens/micro-learning/card/card.container';
import { PaymentHoldContainer } from '~/screens/payment-hold/payment-hold.container';
import { PaymentVerification } from '~/screens/payment-verification/payment-verification.component';
import { ReviewFormContainer } from '~/screens/review-form/review-form.container';
import { SelectUtilityContainer } from '~/screens/select-utility/select-utility.container';
import { VnmTermsContainer } from '~/screens/vnm-terms/vnm-terms.container';
import { AcuityMachine } from '../acuity';
import { BillingMachine } from '../billing-selection';
import { DocuSignMachine } from '../docusign';
import { EnrollmentMachine } from '../enrollment-selection';
import { LegalMachine } from '../legal-completion';
import { LmiReviewMachine } from '../lmi-review';
import { ReviewMachine } from '../review-form';
import { UploadMachine } from '../upload';
import { UploadContractsMachine } from '../upload-contracts';
import { SolarFunnelMachine } from './types';

export type SolarScreenMap = {
  utility: ScreenMap<SolarFunnelMachine, UtilityMachine>;
  user: ScreenMap<SolarFunnelMachine, UserMachine, 'microLearning'>;
  upload: ScreenMap<SolarFunnelMachine, UploadMachine>;
  uploadContracts: ScreenMap<SolarFunnelMachine, UploadContractsMachine>;
  enrollment: ScreenMap<SolarFunnelMachine, EnrollmentMachine>;
  billing: ScreenMap<SolarFunnelMachine, BillingMachine>;
  review: ScreenMap<SolarFunnelMachine, ReviewMachine>;
  acuity: ScreenMap<SolarFunnelMachine, AcuityMachine>;
  legal: ScreenMap<SolarFunnelMachine, LegalMachine>;
  docuSign: ScreenMap<SolarFunnelMachine, DocuSignMachine>;
  fetchFeatureFlags: ScreenMap<SolarFunnelMachine>;
  fetchBillingExperienceOptions: ScreenMap<SolarFunnelMachine>;
  fetchWaitlistOptions: ScreenMap<SolarFunnelMachine>;
  getEnrollmentOptions: ScreenMap<SolarFunnelMachine>;
  waitlistDecider: ScreenMap<SolarFunnelMachine>;
  billingDecider: ScreenMap<SolarFunnelMachine>;
  postBillingSelection: ScreenMap<SolarFunnelMachine>;
  noBilling: ScreenMap<SolarFunnelMachine>;
  completed: ScreenMap<SolarFunnelMachine>;
  docusignTimeoutRedirect: ScreenMap<SolarFunnelMachine>;
  unexpected: ScreenMap<SolarFunnelMachine>;
  lmiZoneVerification: ScreenMap<SolarFunnelMachine>;
  listenForPaymentCreation: ScreenMap<SolarFunnelMachine>;
  verifyPaymentHold: ScreenMap<SolarFunnelMachine>;
  verifyPaymentType: ScreenMap<SolarFunnelMachine>;
  csUnavailable: ScreenMap<SolarFunnelMachine>;
  paymentVerificationFailed: ScreenMap<SolarFunnelMachine>;
  refetchFunnelPacket: ScreenMap<SolarFunnelMachine>;
  updateUtilityAccountDetail: ScreenMap<SolarFunnelMachine>;
  lmiReview: ScreenMap<SolarFunnelMachine, LmiReviewMachine>;
};

export const solarScreenMap: Readonly<SolarScreenMap> = {
  utility: {
    utility: SelectUtilityContainer,
    invalidProjectId: () => {
      return (
        <CsUnavailableContainer
          heading="Sorry, The project you selected is not available"
          subHeading="In the meantime explore our site to learn more about community solar."
        />
      );
    },
    bouncer: () => <Loading />,
    friction: () => <Friction />,
    redirect: () => <Loading />,
    completed: () => <Loading />,
  },
  fetchFeatureFlags: () => <Loading />,
  user: {
    'microLearning.getStarted': CardContainer,
    'microLearning.solarWithoutARoof': CardContainer,
    'microLearning.howItWorks': CardContainer,
    'microLearning.readyToSubscribe': ({ micro, overrideCopy }) => (
      <CardContainer
        micro={micro}
        overrideCopy={overrideCopy}
        overrideProps={{
          done: () => micro.send({ type: 'DONE' }),
        }}
      />
    ),
    'microLearning.completed': () => <Loading />,
    user: CreateAccountContainer,
    google: CreateAccountGoogleContainer,
    completed: () => <Loading />,
  },
  upload: {
    idle: LMIProofDocsUploadContainer,
    submitting: LMIProofDocsUploadContainer,
    completed: () => <Loading />,
  },
  uploadContracts: {
    uploadContracts: ContractsUploadContainer,
    completed: () => <Loading />,
  },
  enrollment: {
    utilityConnect: CredentialsContainer,
    accountNumber: AccountNumberContainer,
    billUpload: BillUploadContainer,
    fallBackAccountNumber: ({ micro, overrideCopy, funnel }) => (
      <AccountNumberContainer
        micro={micro}
        funnel={funnel}
        overrideCopy={overrideCopy}
        overrideProps={{
          error: micro.context.errors.fallBackAccountNumber,
          pending: micro.matches('fallBackAccountNumber.submitting'),
        }}
      />
    ),
    nyLmiZoneOptions: () => <Loading />,
    isInLmiZone: () => <Loading />,
    completed: () => <Loading />,
    ameren: () => <Loading />,
    amerenAuthTimeout: () => <Loading />,
  },

  billing: {
    fetching: () => <Loading />,
    payment: BillingContainer,
    hold: PaymentHoldContainer,
    completed: () => <Loading />,
  },
  review: {
    review: ReviewFormContainer,
    completed: () => <Loading />,
  },
  /* eslint-disable no-unsafe-optional-chaining */
  acuity: {
    review: ({ micro: { context, send }, overrideCopy }) => {
      const funnelPacket = context?.funnelPacket;
      return (
        <Acuity
          context={{
            funnelPacket,
          }}
          next={() => send({ type: 'SCHEDULE_MEET' })}
          overrideCopy={overrideCopy}
        />
      );
    },
    completed: () => <Loading />,
  },
  /* eslint-disable no-unsafe-optional-chaining */
  legal: {
    review: VnmTermsContainer,
    completed: () => <Loading />,
  },
  docuSign: {
    polling: ({ micro, overrideCopy }) => (
      <DocuSignContainer
        micro={micro}
        overrideCopy={overrideCopy}
        overrideProps={{ childStep: 'polling' }}
      />
    ),
    timeout: ({ micro, overrideCopy }) => (
      <DocuSignContainer
        micro={micro}
        overrideCopy={overrideCopy}
        overrideProps={{ childStep: 'timeout' }}
      />
    ),
    ready: () => <Loading />,
    completed: () => <Loading />,
    sendInstantAlert: () => <Loading />,
  },
  waitlistDecider: () => <Loading />,
  billingDecider: () => <Loading />,
  postBillingSelection: () => <Loading />,
  noBilling: () => <Loading />,
  fetchWaitlistOptions: () => <Loading />,
  fetchBillingExperienceOptions: () => <Loading />,
  getEnrollmentOptions: () => <Loading />,
  lmiZoneVerification: () => <Loading />,
  listenForPaymentCreation: () => <PaymentVerification />,
  verifyPaymentHold: () => <Loading />,
  verifyPaymentType: () => <Loading />,
  completed: () => <Loading />,
  docusignTimeoutRedirect: () => <Loading />,
  unexpected: () => <Unexpected />,
  csUnavailable: () => <CsUnavailableContainer />,
  refetchFunnelPacket: () => <Loading />,
  updateUtilityAccountDetail: () => <Loading />,
  paymentVerificationFailed: ({ funnel, overrideCopy }) => (
    <EnergywellPaymentVerificationFailedContainer micro={funnel} overrideCopy={overrideCopy} />
  ),
  lmiReview: {
    lmiReview: LmiReviewContainer,
    completed: () => <Loading />,
    updateUser: () => <Loading />,
  },
};

// NB: do not consume - if this errors, a state is missing a corresponding screen in the map
export const _completenessCheck: Record<
  MachineScreenStates<SolarFunnelMachine>,
  any
> = solarScreenMap;
